import { z } from "zod";
import { recurringScheduleSchema } from "../scheduling/recurringSchedule.js";
import {
  automationDeliverySchema,
  automationIdSchema,
  automationPlaybooksScopeSchema,
  automationRecipientSelectionSchema,
  automationResultModeSchema,
  automationSubscriberRecipientsSchema,
  automationTemplateIdSchema,
  checkIsFormAuthoredReportDefinition,
  checkIsSharedReportDefinition,
  checkIsTeammateCoachingSettings,
  getCoachingEmailRecipients,
  updateAutomationDeliveryEditorValue,
  updateAutomationPeopleSelection,
  updateAutomationPlaybookIds,
  updateAutomationRecipientSelection,
  updateAutomationResultMode,
  updateAutomationSubscribersEditorValue,
  updateCoachingEmailRecipients
} from "./automation.js";
import {
  automationProposalKindSchema,
  copilotProposalIdSchema,
  copilotProposalSummarySchema
} from "./copilotProposal.js";
import { groupIdSchema } from "./group.js";
import { playbookIdSchema } from "./playbook.js";
import { teammateIdSchema } from "./teammate.js";
const automationPeopleProposalSchema = z.object({
  includes_all_teammates: z.boolean(),
  includes_all_managers: z.boolean(),
  teammate_ids: z.array(teammateIdSchema),
  group_ids: z.array(groupIdSchema).optional()
});
function readPeopleProposal(people) {
  return {
    includesAllTeammates: people.includes_all_teammates,
    includesAllManagers: people.includes_all_managers,
    teammateIds: people.teammate_ids,
    groupIds: people.group_ids ?? []
  };
}
const instructionsProposalSchema = z.string().optional();
const reportConfigurationProposalSchema = z.object({
  template_family: z.literal("report"),
  name: z.string().min(1).optional(),
  instructions: instructionsProposalSchema,
  playbook_ids: automationPlaybooksScopeSchema.optional(),
  trigger: recurringScheduleSchema.optional(),
  result_mode: automationResultModeSchema.optional(),
  /** Who receives a shared result. */
  recipients: automationRecipientSelectionSchema.optional(),
  /** Who each get their own result. */
  people: automationPeopleProposalSchema.optional(),
  subscribers: automationSubscriberRecipientsSchema.optional(),
  delivery: automationDeliverySchema.optional()
});
const coachingConfigurationProposalSchema = z.object({
  template_family: z.literal("coaching"),
  name: z.string().min(1).optional(),
  instructions: instructionsProposalSchema,
  playbook_id: playbookIdSchema.optional(),
  /**
   * Shorthand for the "All teammates" / "All managers" options. The summary
   * no longer reads them out, so a fresh proposal writes `email_recipients`;
   * they stay accepted for cards proposed before that.
   */
  should_notify_teammates: z.boolean().optional(),
  should_notify_managers: z.boolean().optional(),
  email_recipients: automationPeopleProposalSchema.optional()
});
const automationConfigurationProposalSchema = z.discriminatedUnion("template_family", [
  reportConfigurationProposalSchema,
  coachingConfigurationProposalSchema
]).refine(
  (configuration) => Object.keys(configuration).some((field) => field !== "template_family"),
  { message: "A proposal must change at least one field" }
);
const newAutomationConfigurationSchema = z.discriminatedUnion(
  "template_family",
  [
    reportConfigurationProposalSchema.extend({
      playbook_ids: z.tuple([playbookIdSchema]).rest(playbookIdSchema)
    }),
    coachingConfigurationProposalSchema.extend({
      playbook_id: playbookIdSchema
    })
  ]
);
function applyAutomationConfigurationProposal(current, configuration, context) {
  const name = configuration.name ?? current.name;
  const { settings } = current;
  if (configuration.template_family === "coaching") {
    if (!checkIsTeammateCoachingSettings(settings)) {
      throw new Error(
        "A coaching configuration proposal cannot configure a report automation"
      );
    }
    const currentPeople = getCoachingEmailRecipients(settings);
    const changesAudiences = configuration.should_notify_teammates !== void 0 && configuration.should_notify_teammates !== currentPeople.includesAllTeammates || configuration.should_notify_managers !== void 0 && configuration.should_notify_managers !== currentPeople.includesAllManagers;
    if (!configuration.email_recipients && changesAudiences && currentPeople.groupIds.length > 0) {
      throw new Error(
        "Specify email_recipients to change an audience selected through groups"
      );
    }
    const recipientSettings = configuration.email_recipients ? updateCoachingEmailRecipients(
      settings,
      readPeopleProposal(configuration.email_recipients)
    ) : changesAudiences ? updateCoachingEmailRecipients(settings, {
      includesAllTeammates: configuration.should_notify_teammates ?? currentPeople.includesAllTeammates,
      includesAllManagers: configuration.should_notify_managers ?? currentPeople.includesAllManagers,
      teammateIds: currentPeople.teammateIds.filter(
        (id) => context.managerTeammateIds.includes(id) ? configuration.should_notify_managers === void 0 : configuration.should_notify_teammates === void 0
      ),
      groupIds: []
    }) : settings;
    return {
      name,
      settings: {
        ...recipientSettings,
        playbook_id: configuration.playbook_id ?? settings.playbook_id,
        coaching_focus: configuration.instructions ?? settings.coaching_focus
      }
    };
  }
  if (checkIsTeammateCoachingSettings(settings)) {
    throw new Error(
      "A report configuration proposal cannot configure a coaching automation"
    );
  }
  let next = settings;
  if (configuration.result_mode) {
    next = updateAutomationResultMode(next, configuration.result_mode);
  }
  if (configuration.playbook_ids) {
    next = updateAutomationPlaybookIds(next, configuration.playbook_ids);
  }
  if (configuration.trigger) {
    next = { ...next, trigger: configuration.trigger };
  }
  if (configuration.recipients) {
    next = updateAutomationRecipientSelection(next, configuration.recipients);
  }
  if (configuration.people) {
    const definition = next.report_definition;
    if (!checkIsFormAuthoredReportDefinition(definition) || checkIsSharedReportDefinition(definition)) {
      throw new Error(
        "A people selection cannot configure a shared or API-authored report"
      );
    }
    next = updateAutomationPeopleSelection(
      next,
      readPeopleProposal(configuration.people),
      context.managerTeammateIds
    );
  }
  if (configuration.delivery) {
    next = updateAutomationDeliveryEditorValue(next, configuration.delivery);
  }
  if (configuration.subscribers) {
    if (next.report_definition.routing.type !== "instance_recipient") {
      throw new Error(
        "Subscribers cannot configure a shared or channel-delivered report"
      );
    }
    next = updateAutomationSubscribersEditorValue(
      next,
      configuration.subscribers
    );
  }
  if (configuration.instructions !== void 0) {
    next = { ...next, instructions: configuration.instructions };
  }
  return { name, settings: next };
}
function readAutomationInstructions(settings) {
  return checkIsTeammateCoachingSettings(settings) ? settings.coaching_focus : settings.instructions;
}
const proposeAutomationToolInputSchema = z.object({
  change: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("create_automation"),
      proposal_id: copilotProposalIdSchema.optional().describe(
        "When refining an automation that has not been created yet, the pending create_automation proposal to replace. Submit the whole revised draft. Omit only for a separate new automation."
      ),
      template_id: automationTemplateIdSchema.describe(
        "Which template the automation is built from. It cannot be changed afterwards."
      ),
      name: z.string().min(1),
      configuration: newAutomationConfigurationSchema.describe(
        "The automation's configuration, its instructions included. Fields left out keep the template's default."
      ),
      summary: copilotProposalSummarySchema.describe(
        "One plain sentence on what the automation does. Shown on the proposal card."
      )
    }),
    z.object({
      kind: z.literal("update_automation"),
      automation_id: automationIdSchema,
      proposal_id: copilotProposalIdSchema.optional().describe(
        "The pending update_automation proposal on this automation from this conversation to build on: fields you leave out keep its proposed values. Pass your latest proposal's id when refining it; omit to propose changes against the saved automation only."
      ),
      configuration: automationConfigurationProposalSchema.describe(
        "Only the fields that change, instructions included. Fields left out keep what the automation has saved."
      ),
      summary: copilotProposalSummarySchema.describe(
        "One plain sentence on what this proposal changes compared with the saved automation, including the changes it keeps from the proposal named by proposal_id. Shown on the proposal card."
      )
    })
  ]).describe(
    "The change to propose: a whole new automation (`create_automation`), or changes to an existing one (`update_automation`). Either builds on the proposal named by `proposal_id`, or starts fresh without one."
  )
});
const proposeAutomationToolOutputSchema = z.object({
  proposal_id: copilotProposalIdSchema,
  automation_id: automationIdSchema,
  kind: automationProposalKindSchema
});
export {
  applyAutomationConfigurationProposal,
  automationConfigurationProposalSchema,
  newAutomationConfigurationSchema,
  proposeAutomationToolInputSchema,
  proposeAutomationToolOutputSchema,
  readAutomationInstructions
};
//# sourceMappingURL=automationProposal.js.map
